import { GroupeFormData } from "@repo/types";
import { Alert, AlertDescription } from "@repo/ui/components/ui/alert";
import { Button } from "@repo/ui/components/ui/button";
import { Card } from "@repo/ui/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@repo/ui/components/ui/tabs";
import type { SubscriptionConfig } from "@repo/ui/lib/api/services/settings.service";
import { Check, Info, Loader2, Package, X } from "lucide-react";
import { GroupeInfoSection } from "./shared/groupe-info-section";
import { GroupePlanSection } from "./shared/groupe-plan-section";

interface CreateGroupeFormFieldsProps {
  formData: GroupeFormData;
  error: string | null;
  isPending: boolean;
  subscriptionConfig?: SubscriptionConfig;
  selectedPlanType: 'BASIC' | 'ADVANCED';
  selectedTierId: string;
  onCancel: () => void;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelectChange: (name: string, value: string) => void;
  handleSubmit: (e: React.FormEvent) => void;
  handlePlanSelect: (planType: 'BASIC' | 'ADVANCED', tierId: string) => void;
}

export function CreateGroupeFormFields({
  formData,
  error,
  isPending,
  subscriptionConfig,
  selectedPlanType,
  selectedTierId,
  onCancel,
  handleInputChange,
  handleSelectChange,
  handleSubmit,
  handlePlanSelect
}: CreateGroupeFormFieldsProps) {
  
  // Check if PREMIUM type requires plan selection
  const isPremiumType = formData.groupe_type === 'PREMIUM';
  const hasPlanSelected = selectedTierId && selectedTierId.length > 0;
  const isPremiumWithoutPlan = isPremiumType && !hasPlanSelected;
  
  // Determine if form can be submitted
  const canSubmit = formData.name && (!isPremiumType || hasPlanSelected);
  
  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Compact Header */}
      <div>
        <h2 className="text-2xl font-bold">Créer un nouveau groupe</h2>
        <p className="text-muted-foreground mt-1">
          Configurez votre groupe en remplissant les informations
        </p>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription className="flex items-center gap-2">
            <X className="h-4 w-4" />
            {error}
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Card className="p-6">
          <Tabs defaultValue="info" className="w-full">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Vertical Tabs */}
              <TabsList className="flex flex-col md:w-48 w-full h-fit p-1.5 bg-muted/50 rounded-lg border border-border/50">
                <TabsTrigger
                  value="info"
                  className="flex items-center gap-3 w-full justify-start px-4 py-2.5 h-11 rounded-md text-sm font-medium transition-all data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm data-[state=inactive]:text-muted-foreground hover:text-foreground hover:bg-muted"
                >
                  <Info className="h-4 w-4 shrink-0" />
                  <span>Informations</span>
                </TabsTrigger>
                <TabsTrigger
                  value="plan"
                  className="flex items-center gap-3 w-full justify-start px-4 py-2.5 h-11 rounded-md text-sm font-medium transition-all data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm data-[state=inactive]:text-muted-foreground hover:text-foreground hover:bg-muted"
                >
                  <Package className="h-4 w-4 shrink-0" />
                  <span>Abonnement</span>
                  {isPremiumType && (
                    <span className="ml-auto text-xs font-semibold text-red-500">*</span>
                  )}
                </TabsTrigger>
              </TabsList>

              {/* Content Area */}
              <div className="flex-1 min-h-[400px]">
                <TabsContent value="info" className="mt-0">
                  <GroupeInfoSection
                    formData={formData}
                    handleInputChange={handleInputChange}
                    handleSelectChange={handleSelectChange}
                  />
                </TabsContent>

                <TabsContent value="plan" className="mt-0">
                  <div className="space-y-4">
                    {isPremiumType && (
                      <Alert>
                        <Info className="h-4 w-4" />
                        <AlertDescription>
                          Le type de groupe <strong>PREMIUM</strong> nécessite la sélection d'un plan d'abonnement.
                        </AlertDescription>
                      </Alert>
                    )}
                    
                    {subscriptionConfig ? (
                      <GroupePlanSection
                        subscriptionConfig={subscriptionConfig}
                        selectedPlanType={selectedPlanType}
                        selectedTierId={selectedTierId}
                        onPlanSelect={handlePlanSelect}
                      />
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        Chargement des plans...
                      </div>
                    )}
                    
                    {isPremiumWithoutPlan && (
                      <Alert variant="destructive">
                        <X className="h-4 w-4" />
                        <AlertDescription>
                          Veuillez sélectionner un plan d'abonnement pour continuer.
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                </TabsContent>
              </div>
            </div>
          </Tabs>
        </Card>

        {/* Actions */}
        <div className="flex justify-between pt-6">
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            disabled={isPending}
          >
            <X className="h-4 w-4 mr-2" />
            Annuler
          </Button>

          <Button
            type="submit"
            disabled={isPending || !canSubmit}
            className="min-w-[160px]"
            title={isPremiumWithoutPlan ? "Sélectionnez un plan d'abonnement pour continuer" : ""}
          >
            {isPending ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                En cours...
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4" />
                Créer le groupe
              </div>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}

